class UrlUtils {
    /**
     * Adds a query parameter to the current URL and updates the browser's history state.
     *
     * @param {string} key - The name of the query parameter to add.
     * @param {string} value - The value of the query parameter to add.
     */
    static addQueryParam(key, value) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set(key, value);
        const url = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState({}, '', url);
    }

    /**
     * Appends a query parameter to the current URL without reloading the page.
     *
     * @param {string} key - The name of the query parameter to append.
     * @param {string} value - The value of the query parameter to append.
     */
    static appendQueryParam(key, value) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.append(key, value);
        const url = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState({}, '', url);
    }

    /**
     * Removes a query parameter from the current URL.
     *
     * @param {string} key - The key of the query parameter to remove.
     */
    static removeQueryParam(key) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(key);
        window.history.replaceState({}, '', url);
    }
    /**
     * Retrieves the query parameters from the current URL.
     *
     * @returns {URLSearchParams} An instance of URLSearchParams containing the query parameters.
     */
    static getQueryParams() {
        return new URLSearchParams(window.location.search);
    }
    /**
     * Retrieves the value of a specified query parameter from the current URL.
     *
     * @param {string} key - The name of the query parameter to retrieve.
     * @returns {string|null} The value of the query parameter, or null if the parameter is not present.
     */
    static getQueryParam(key) {
        return new URLSearchParams(window.location.search).get(key);
    }
}
class CookieUtils{
    /**
     * Retrieves the value of a specified cookie by its name.
     *
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string|undefined} The value of the cookie if found, otherwise undefined.
     */
    static getCookie(name) {
        const value = "; " + document.cookie;
        const parts = value.split("; " + name + "=");
        if (parts.length == 2) return parts.pop().split(";").shift();
    }
    /**
     * Sets a cookie with the specified name, value, and expiration days.
     *
     * @param {string} name - The name of the cookie.
     * @param {string} value - The value of the cookie.
     * @param {number} days - The number of days until the cookie expires.
     * @param {string} [domain=null] - The domain for which the cookie is valid. Defaults to null.
     */
    static setCookie(name, value, days, domain = null) {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = "; expires=" + date.toUTCString();
        }
        if (domain) {
            document.cookie = name + "=" + value + expires + "; domain=" + domain + "; path=/";
        }
        else {
            document.cookie = name + "=" + value + expires + "; path=/";
        }
    }
    /**
     * Deletes a cookie with the specified name.
     *
     * @param {string} name - The name of the cookie to delete.
     */
    static deleteCookie(name) {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
}
class LocalStorageUtils {
    /**
     * Stores a value in the local storage.
     *
     * @param {string} key - The key under which the value is stored.
     * @param {string} value - The value to be stored.
     */
    static setItem(key, value) {
        localStorage.setItem(key, value);
    }
    /**
     * Retrieves an item from local storage.
     *
     * @param {string} key - The key of the item to retrieve.
     * @returns {string | null} The value of the item, or null if the item does not exist.
     */
    static getItem(key) {
        return localStorage.getItem(key);
    }
}
class DateUtils {
    /**
     * Gets the current date formatted as 'YYYY-MM-DD' in the specified timezone.
     *
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York').
     * @returns {string} The current date in the format 'YYYY-MM-DD'.
     */
    static getCurrentDate(timezone) {
        const date = new Date();
        const year = date.toLocaleString('default', {
            timeZone: timezone,
            year: 'numeric'
        });
        const month = date.toLocaleString('default', {
            timeZone: timezone,
            month: '2-digit',
        });
        const day = date.toLocaleString('default', {
            timeZone: timezone,
            day: '2-digit'
        });
        return [year, month, day].join('-');
    }
    /**
     * Checks if the current date is between the given start and end dates.
     *
     * @param {Date} startDate - The start date to compare.
     * @param {Date} endDate - The end date to compare.
     * @returns {boolean} - Returns true if the current date is between the start and end dates, otherwise false.
     */
    static checkDateBetween(timezone, startDate, endDate) {
        if (!startDate && !endDate) return true;
        const bounds = [startDate, endDate].map(value => (value || '').trim().replace('T', ' '));
        for (const value of bounds) {
            if (!value) continue;
            if (!/^\d{4}-\d{2}-\d{2}(?: (?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?)?$/.test(value)) return false;
            const day = new Date(value.slice(0, 10) + 'T00:00:00Z');
            if (Number.isNaN(day.getTime()) || day.toISOString().slice(0, 10) !== value.slice(0, 10)) return false;
        }
        const from = bounds[0] && bounds[0].padEnd(19, bounds[0].length === 10 ? ' 00:00:00' : ':00');
        const to = bounds[1] && bounds[1].padEnd(19, bounds[1].length === 10 ? ' 23:59:59' : ':59');
        try {
            const date = this.getCurrentDateTime(timezone || 'UTC');
            return (!from || date >= from) && (!to || date <= to);
        } catch (error) {
            return false;
        }
    }

    /**
     * Gets the current date time formatted as 'YYYY-MM-DD hh:mm:ss' in the specified timezone.
     *
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York').
     * @returns {string} - The current date time in the format 'YYYY-MM-DD hh:mm:ss'.
     */
    static getCurrentDateTime(timezone) {
        const date = new Date();
        let year = date.toLocaleString('default', {
            timeZone: timezone,
            year: 'numeric'
        });
        let month = date.toLocaleString('default', {
            timeZone: timezone,
            month: '2-digit',
        });
        let day = date.toLocaleString('default', {
            timeZone: timezone,
            day: '2-digit'
        });

        let hour = date.toLocaleTimeString('default', {
            timeZone: timezone,
            hour: '2-digit',
            hourCycle: 'h23'
        });

        let minute = date.toLocaleTimeString('default', {
            timeZone: timezone,
            minute: '2-digit'
        });
        let second = date.toLocaleTimeString('default', {
            timeZone: timezone,
            second: '2-digit'
        });

        if (minute.length == 1) {
            minute = '0' + minute;
        }
        if (second.length == 1) {
            second = '0' + second;
        }

        return [year, month, day].join('-') + ' ' + [hour, minute, second].join(':');
    }


    /**
     * Returns an array containing the values to be shown in the countdown
     *
     * @param {Date} countDownDate - The date that i want to set the countdown for
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York').
     * @returns {Array} - Returns an array of three elements like this ["24h","59m","59s"]
     */
    static getCountdown(countDownDate, timezone) {
        const currentDateTime = this.getCurrentDateTime(timezone);
        const currentTimestamp = new Date(currentDateTime.replace(' ', 'T')).getTime();
        const distance = countDownDate - currentTimestamp;

        if (distance < 0) {
            return [];
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        let countdownValues = [];

        if (days > 0) {
            countdownValues.push(days + " d");
            countdownValues.push(hours + " h");
            countdownValues.push(minutes + " m");
            countdownValues.push(seconds + " s");
        } else {
            if (hours > 0) {
                countdownValues.push(hours + " h");
                countdownValues.push(minutes + " m");
                countdownValues.push(seconds + " s");
            } else {
                if (minutes > 0) {
                    countdownValues.push(minutes + " m");
                    countdownValues.push(seconds + " s");
                } else {
                    countdownValues.push(seconds + " s");
                }
            }
        }

        return countdownValues;
    }
}
class Fetchutils{

    /**
     * Fetches data from a specified URL and returns the response as JSON.
     *
     * @param {string} url - The URL to fetch the data from.
     * @returns {Promise<any>} A promise that resolves with the JSON response data.
     */
    static async fetchJson(url) {
        const response = await fetch(url);
        return response.json();
    }

    /**
     * Fetches HTML content from a given URL and parses it into a Document object.
     *
     * @param {string} url - The URL to fetch the HTML content from.
     * @returns {Promise<Document>} A promise that resolves to a Document object containing the parsed HTML.
     */
    static async fetchHtml(url) {
        const response = await fetch(url);
        const text = await response.text();
        return new DOMParser().parseFromString(text, 'text/html');
    }

    /**
     * Sends a POST request with JSON data to the specified URL and returns the JSON response.
     *
     * @param {string} url - The URL to send the POST request to.
     * @param {Object} data - The data to be sent in the body of the POST request.
     * @returns {Promise<Object>} - A promise that resolves to the JSON response.
     */
    static async postJson(url, data) {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });

        return response.json();
    }


    /**
     * Sends a POST request to a GraphQL endpoint.
     *
     * @param {string} url - The URL of the GraphQL endpoint.
     * @param {string} token - The Shopify Storefront Access Token.
     * @param {string} query - The GraphQL query to be sent.
     * @returns {Promise<Object>} - A promise that resolves to the JSON response from the server.
     */
    static async postGraphql(url, token, query) {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                //'Content-Type': 'application/graphql',
                'X-Shopify-Storefront-Access-Token': token
            },
            body: JSON.stringify({ query })
        });

        return response.json();
    }

    /**
     * Imports a script dynamically by creating a script element and appending it to the document body.
     *
     * @param {string} url - The URL the script to be imported.
     * @returns {Promise<void>} - A promise that resolves when the script is loaded.
     */
    static async importScript(url) {
        if (!url) {
            throw new Error("URL is required to import a script.");
        }
        const scriptEl = document.createElement('script');
        scriptEl.src = url;
        scriptEl.async = true;
        document.body.appendChild(scriptEl);

        return new Promise((resolve, reject) => {
            scriptEl.onload = () => {
                resolve();
            };
            scriptEl.onerror = (error) => {
                reject(error);
            };
        });
    }

    /**
     * Creates a detached copy of plain analytics data, including nested objects and arrays.
     *
     * @param {*} value - The value to copy.
     * @returns {*} A detached copy for plain analytics data.
     */
    static createIsolatedTealiumPayload(value) {
        if (Array.isArray(value)) {
            return value.map(Fetchutils.createIsolatedTealiumPayload);
        }

        if (!value || typeof value !== "object") {
            return value;
        }

        if (Object.prototype.toString.call(value) !== "[object Object]") {
            return value;
        }

        return Object.entries(value).reduce((payload, [key, nestedValue]) => {
            payload[key] = Fetchutils.createIsolatedTealiumPayload(nestedValue);
            return payload;
        }, {});
    }

    /**
     * Sends data to Tealium for tracking purposes.
     *
     * @param {object} obj - The data to be sent to Tealium.
     * @returns {Promise<void>} - A promise that resolves when the data is sent.
     */
    static async sendTealiumData(obj) {
        if (!obj || typeof obj !== "object") {
            throw new Error("Valid object is required to send Tealium data.");
        }
        if (typeof tealium_data2track === "undefined" || typeof tealium_data2track.push !== "function") {
            console.error("Tealium data tracking function is not defined.");
            return;
        }

        const payload = typeof window.normalizeTealiumProductPayload === 'function'
            ? window.normalizeTealiumProductPayload(obj) : obj;
        tealium_data2track.push(Fetchutils.createIsolatedTealiumPayload(payload));
    }
}
class ObserverUtils{
    /**
     * Creates a new IntersectionObserver instance.
     *
     * @param {IntersectionObserverCallback} callback - The function to be called when the observed elements intersect with the viewport.
     * @param {IntersectionObserverInit} options - Optional configuration options for the IntersectionObserver.
     * @returns {IntersectionObserver} A new IntersectionObserver instance.
     */
    static createIntersectionObserver(callback, options) {
        return new IntersectionObserver(callback, options);
    }

    /**
     * Creates a new MutationObserver instance.
     *
     * @param {MutationCallback} callback - The function to be called when mutations are observed.
     * @param {MutationObserverInit} options - Options for configuring the observer.
     * @returns {MutationObserver} A new MutationObserver instance.
     */
    static createMutationObserver(callback, options) {
        return new MutationObserver(callback, options);
    }

    /**
     * Waits for an element to be added to the DOM.
     *
     * @param {string} selector - The selector of the element to wait for.
     * @param {Element} container - The container element to search within, by default it's document.body.
     * @param {number} timeout - Optional timeout in milliseconds. If provided, the promise will reject if the element is not found within this time.
     * @returns {Promise<Element>} A promise that resolves with the found element or rejects if the element is not found within the timeout (if declared).
     */
    static waitForElement(selector, container = document.body, timeout) {
        return new Promise((resolve, reject) => {
            const observer = new MutationObserver((mutationsList) => {
                for (const mutation of mutationsList) {
                    if (mutation.type === 'childList') {
                        if (container.querySelector(selector)) {
                            resolve(document.querySelector(selector));
                            observer.disconnect();
                            return;
                        }
                    }
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            if (!timeout) return;
            setTimeout(() => {
                reject(new Error(`Timeout waiting for element: ${selector}`));
            }, timeout);
        });
    }
}
class PubSub{
    static subscribers = {};
    /**
     * Publishes an event to all subscribers.
     *
     * @param {string} eventName - The name of the event to publish.
     * @param {*} data - The data to pass to the event subscribers.
     */
    static publish(eventName, data) {
        if (subscribers[eventName]) {
            subscribers[eventName].forEach((callback) => {
                callback(data);
            });
        }
    }
    /**
     * Subscribes a callback function to a specific event.
     *
     * @param {string} eventName - The name of the event to subscribe to.
     * @param {Function} callback - The callback function to be executed when the event is triggered.
     * @returns {Function} A function to unsubscribe the callback from the event.
     */
    static subscribe(eventName, callback) {
        if (subscribers[eventName] === undefined) {
            subscribers[eventName] = [];
        }
        subscribers[eventName] = [...subscribers[eventName], callback];
        return function unsubscribe() {
            subscribers[eventName] = subscribers[eventName].filter((cb) => {
                return cb !== callback;
            });
        };
    }
}

class GeneralUtils {
    /**
     * Stores a value in the local storage.
     * Original function taken from: https://github.com/mktj/parsecurrency
     * License: https://github.com/mktj/parsecurrency?tab=MIT-1-ov-file
     *
     * @param {string} priceStr - The price string to be parsed.
     * @returns {Object|null} An object containing parsed currency details or null if parsing fails.
     */
    static parseCurrency(priceStr) {
        const currencyMatcher = /^(?:([-+]{1}) ?)?(?:([A-Z]{3}) ?)?(?:([^\d ]+?) ?)?(?:([-+]{1}) ?)?(((?:\d{1,3}([,. ’'\u00A0\u202F]))*?\d{1,})(([,.])\d{1,2})?)(?: ?([^\d]+?))??(?: ?([A-Z]{3}))?$/;
        const gr = /^\d{1,3}([,. ’'\u00A0\u202F]\d{3})*$/; // validate groups
        const ind = /^\d{1,2}(,\d{2})*(,\d{3})?$/; // exception for Indian number format

        if (!priceStr || !priceStr.match) return null;
        priceStr = priceStr.trim();
        const match = priceStr.match(currencyMatcher);
        if (!match) return null;
        const groupSeparator = match[7] || '';
        const decimalSeparator = match[9] || '';
        if (groupSeparator === decimalSeparator && decimalSeparator) return null;
        const isNeg = match[1] === '-' || match[4] === '-';
        const integer = isNeg ? '-' + match[6] : match[6];
        if (groupSeparator && !match[6].match(gr) && !match[6].match(ind)) return null;
        let value = match[5];
        if (!value) return null;
        if (groupSeparator) value = value.replace(RegExp('\\' + groupSeparator, 'g'), '');
        if (decimalSeparator) value = value.replace(decimalSeparator, '.');
        const numericVal = isNeg ? value * -1 : +value;
        if (typeof numericVal !== 'number' || isNaN(numericVal)) return null;

        return {
            raw: priceStr,
            value: numericVal,
            integer: integer || '',
            decimals: match[8] || '',
            currency: match[2] || match[11] || '',
            symbol: match[3] || match[10] || '',
            decimalSeparator: decimalSeparator,
            groupSeparator: groupSeparator,
            sign: match[1] || match[4] || ''
        };
    };

    /**
     * Creates and returns a debounced version of the provided function that delays its execution until after
     * a specified wait time has elapsed since the last time it was invoked.
     *
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The number of milliseconds to delay.
     * @returns {Function} A debounced version of the provided function.
     */
    static debounce(func, wait) {
        let timer;
        return (...args) =>
            new Promise((resolve) => {
                clearTimeout(timer);
                timer = setTimeout(() => resolve(func(...args)), wait);
            });
    }

    /**
     * Check if desktop
     */
    static isDesktop() {
        return window.matchMedia('(min-width: 1024px)').matches;
    }
}
